import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { games } from "@/mocks/games";
import { getReview } from "@/mocks/reviews";

const categoryName: Record<string, string> = {
  campus: "校园纯爱", fantasy: "奇幻冒险", city: "都市职场", thriller: "悬疑惊悚",
  harem: "后宫多线", healing: "治愈日常", dark: "暗黑题材", world: "架空世界观",
};

export default function ReviewDetail() {
  const { id } = useParams();
  const game = games.find((g) => g.id === id);
  const review = getReview(id || "");

  useEffect(() => {
    document.title = game ? `${game.title} 评测 | 幻夜志` : "评测 | 幻夜志";
  }, [game]);

  if (!game) {
    return (
      <section className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center gap-4 px-4 py-24 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
        <h1 className="font-display text-xl font-bold text-foreground-950">没有找到这篇评测</h1>
        <Link to="/ranking" className="rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50">
          返回排行榜
        </Link>
      </section>
    );
  }

  const related = games.filter((g) => g.id !== game.id).slice(0, 3);

  return (
    <>
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img src={game.cover} alt={`${game.title} 封面`} className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-background-50"></div>
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-14 pt-12 md:px-6 md:pb-16 md:pt-16">
          <nav aria-label="面包屑" className="text-xs text-background-100/80">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="hover:text-background-50">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to="/ranking" className="hover:text-background-50">排行榜</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="font-medium text-background-50">{game.title}</li>
            </ol>
          </nav>
          <div className="mt-6 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">{categoryName[game.category]}</span>
              {game.isMasterpiece && <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">神作</span>}
              {game.isBeginner && <span className="rounded-full bg-background-50/90 px-3 py-1 text-xs font-medium text-foreground-800">入门推荐</span>}
            </div>
            <h1 className="mt-4 font-display text-3xl font-black text-background-50 md:text-4xl">{game.title}</h1>
            <p className="mt-2 text-sm text-background-100/80">{game.sub} · {game.studio} · {game.year}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-background-100/90">
              <span className="inline-flex items-center gap-2"><i className="ri-star-fill text-primary-300"></i>口碑分 <b className="font-display text-lg text-primary-300">{game.score.toFixed(1)}</b></span>
              <span className="inline-flex items-center gap-2"><i className="ri-gamepad-line"></i>{game.platform}</span>
              <span className="inline-flex items-center gap-2"><i className="ri-timer-line"></i>{game.isMasterpiece ? "40h+" : "8—15h"} 预估时长</span>
            </div>
          </div>
        </div>
      </section>

      <article className="mx-auto grid w-full max-w-7xl gap-10 px-4 pb-16 md:px-6 lg:grid-cols-[1.7fr_1fr]">
        <div className="min-w-0">
          {review && (
            <>
              <p className="border-l-4 border-accent-500 pl-4 font-display text-lg font-bold leading-relaxed text-foreground-900 md:text-xl">
                {review.lead}
              </p>

              <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-background-200/70 bg-background-100 p-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-display text-lg font-bold text-foreground-950">编辑结论</h2>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">{review.verdict}</p>
                </div>
                <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <span className="font-display text-2xl font-black">{game.score.toFixed(1)}</span>
                  <span className="text-[10px]">综合口碑</span>
                </div>
              </div>

              <h2 className="mt-10 font-display text-xl font-bold text-foreground-950">分项评分</h2>
              <div className="mt-4 space-y-3 rounded-2xl border border-background-200/70 bg-background-100 p-6">
                {review.scoreDetail.map((s) => (
                  <div key={s.label} className="flex items-center gap-3">
                    <span className="w-14 shrink-0 text-sm text-foreground-600">{s.label}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-background-200">
                      <div className="h-full rounded-full bg-primary-500" style={{ width: `${s.value * 10}%` }}></div>
                    </div>
                    <span className="w-10 shrink-0 text-right text-sm font-bold text-foreground-950">{s.value.toFixed(1)}</span>
                  </div>
                ))}
              </div>

              {review.sections.map((sec) => (
                <div key={sec.heading} className="mt-9">
                  <h2 className="font-display text-xl font-bold text-foreground-950">{sec.heading}</h2>
                  {sec.paragraphs.map((p, i) => (
                    <p key={i} className="mt-3 text-[15px] leading-loose text-foreground-700">{p}</p>
                  ))}
                </div>
              ))}

              {review.scenes.length > 0 && (
                <div className="mt-9 grid gap-4 sm:grid-cols-2">
                  {review.scenes.map((src, i) => (
                    <img key={i} src={src} alt={`${game.title} 场景 ${i + 1}`} className="h-52 w-full rounded-xl object-cover object-top" />
                  ))}
                </div>
              )}

              <div className="mt-9 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-primary-200 bg-primary-50/60 p-6">
                  <h3 className="inline-flex items-center gap-2 font-display text-base font-bold text-primary-800">
                    <i className="ri-checkbox-circle-line"></i> 优点
                  </h3>
                  <ul className="mt-3 space-y-2 text-sm text-foreground-700">
                    {game.pros.map((p) => (
                      <li key={p} className="flex gap-2"><i className="ri-check-line mt-0.5 text-primary-600"></i>{p}</li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl border border-accent-200 bg-accent-50/60 p-6">
                  <h3 className="inline-flex items-center gap-2 font-display text-base font-bold text-accent-800">
                    <i className="ri-error-warning-line"></i> 不足
                  </h3>
                  <ul className="mt-3 space-y-2 text-sm text-foreground-700">
                    {game.cons.map((c) => (
                      <li key={c} className="flex gap-2"><i className="ri-close-circle-line mt-0.5 text-accent-600"></i>{c}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}

          <div className="mt-9 flex flex-wrap gap-2">
            {game.tags.map((t) => (
              <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-700">{t}</span>
            ))}
          </div>
        </div>

        <aside className="min-w-0 lg:pt-2">
          <div className="rounded-2xl border border-background-200/70 bg-background-100 p-6">
            <img src={game.cover} alt={`${game.title} 封面`} className="w-full rounded-xl object-cover object-top" />
            <h2 className="mt-4 font-display text-lg font-bold text-foreground-950">{game.title}</h2>
            <p className="mt-1 text-xs text-foreground-500">{game.sub}</p>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-foreground-500">开发商</dt><dd className="font-medium text-foreground-800">{game.studio}</dd></div>
              <div className="flex justify-between"><dt className="text-foreground-500">发售年份</dt><dd className="font-medium text-foreground-800">{game.year}</dd></div>
              <div className="flex justify-between"><dt className="text-foreground-500">类型</dt><dd className="font-medium text-foreground-800">{categoryName[game.category]}</dd></div>
              <div className="flex justify-between"><dt className="text-foreground-500">平台</dt><dd className="font-medium text-foreground-800">{game.platform}</dd></div>
            </dl>
          </div>

          <div className="mt-6 rounded-2xl border border-background-200/70 bg-background-100 p-6">
            <h2 className="font-display text-base font-bold text-foreground-950">相关推荐</h2>
            <div className="mt-4 space-y-3">
              {related.map((r) => (
                <Link key={r.id} to={`/review/${r.id}`} className="group flex items-center gap-3">
                  <img src={r.cover} alt={`${r.title} 封面`} className="h-14 w-11 shrink-0 rounded-md object-cover object-top" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground-800 group-hover:text-accent-700">{r.title}</div>
                    <div className="mt-0.5 text-xs text-foreground-500">{categoryName[r.category]} · {r.score.toFixed(1)} 分</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </article>
    </>
  );
}